import { getIn, useFormikContext } from "formik";
import { MouseEvent, useState } from "react";

function collectErrorPaths(errors: unknown, prefix: string): string[] {
  if (errors === null || errors === undefined) {
    return [];
  }

  if (typeof errors === "string") {
    return [prefix];
  }

  if (Array.isArray(errors)) {
    return errors.flatMap((value, index) =>
      collectErrorPaths(value, `${prefix}[${index}]`),
    );
  }

  if (typeof errors === "object") {
    return Object.entries(errors).flatMap(([key, value]) =>
      collectErrorPaths(value, prefix ? `${prefix}.${key}` : key),
    );
  }

  return [];
}

export function useFormSteps<TStep extends string>(
  stepFields: Record<TStep, readonly string[]>,
) {
  const { validateForm, setFieldTouched, submitForm } = useFormikContext();
  const [stepIndex, setStepIndex] = useState(0);

  const stepOrder = Object.keys(stepFields) as TStep[];
  const step = stepOrder[stepIndex];
  const isLast = stepIndex === stepOrder.length - 1;

  async function goNext() {
    const errors = await validateForm();
    const invalidPaths = stepFields[step].flatMap((field) =>
      collectErrorPaths(getIn(errors, field), field),
    );

    if (invalidPaths.length > 0) {
      invalidPaths.forEach((path) => setFieldTouched(path, true, false));
      return;
    }

    setStepIndex((index) => Math.min(index + 1, stepOrder.length - 1));
  }

  async function submitStep(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault();

    const isRepeatClick = event.detail > 1;
    if (isRepeatClick) {
      return;
    }

    if (isLast) {
      await submitForm();
      return;
    }

    await goNext();
  }

  function goBack() {
    setStepIndex((index) => Math.max(index - 1, 0));
  }

  return {
    stepOrder,
    step,
    stepIndex,
    setStepIndex,
    isLast,
    submitStep,
    goBack,
  };
}
